import React, { useEffect, useState } from "react";
import { IconButton, Tooltip } from "@mui/material";
import { Heart } from "lucide-react";
import sh from "bundles/common/utils/sh";

// Per-internal-user follow toggle for a Candidate or Company. Optimistic like the
// pipeline pin toggle: flips immediately and reverts on error. `followed` is the
// initial state; `onChange(next)` lets the parent keep its own copy in sync.
const FollowButton = ({
  followableType,
  followableId,
  followed = false,
  onChange,
  size = 18,
}) => {
  const [isFollowed, setIsFollowed] = useState(Boolean(followed));
  const [saving, setSaving] = useState(false);

  // Sync when the parent supplies a new value (e.g. detail page loads its record
  // async, or the followed-ids set updates). Skipped mid-save to avoid clobbering
  // the optimistic state.
  useEffect(() => {
    if (!saving) setIsFollowed(Boolean(followed));
  }, [followed]);

  const toggle = (event) => {
    event.stopPropagation();
    if (saving || !followableId) return;

    const next = !isFollowed;
    setIsFollowed(next);
    setSaving(true);
    onChange?.(next);

    sh.post(`/internal/follows/${followableType}/${followableId}/toggle`, { followed: next })
      .then((res) => {
        const confirmed = Boolean(res?.data?.followed);
        setIsFollowed(confirmed);
        onChange?.(confirmed);
      })
      .catch(() => {
        setIsFollowed(!next);
        onChange?.(!next);
      })
      .finally(() => setSaving(false));
  };

  const label = isFollowed ? "Following" : "Follow";

  return (
    <Tooltip title={label} disableInteractive>
      <IconButton
        onClick={toggle}
        disableRipple
        size="small"
        aria-label={label}
        sx={{
          color: isFollowed ? "hsl(0, 72%, 51%)" : "hsl(150 10% 46%)",
          "&:hover": { color: "hsl(0, 72%, 51%)", backgroundColor: "transparent" },
        }}
      >
        <Heart size={size} fill={isFollowed ? "currentColor" : "none"} />
      </IconButton>
    </Tooltip>
  );
};

export default FollowButton;
